'use client'

import 'leaflet/dist/leaflet.css'

import { useEffect, useRef, useState } from 'react'
import type {
  Map as LeafletMap,
  CircleMarker as LeafletCircleMarker,
} from 'leaflet'
import cx from 'classnames'
import Image from 'next/image'
import pops from '@stellate/fastly-pops'

export function Map({ className }: { className?: string }): JSX.Element {
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const mapRef = useRef<LeafletMap | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const markersRef = useRef<LeafletCircleMarker[]>([])

  useEffect(() => {
    let destroyed = false
    ;(async () => {
      const L = await import('leaflet')

      if (!containerRef.current || destroyed) return

      const map = L.map(containerRef.current, {
        zoomControl: true, // show +/-
        attributionControl: true,
        scrollWheelZoom: false, // desktop wheel off; mobile pinch still works
        touchZoom: true, // enable pinch-zoom
        dragging: true, // allow dragging
        tap: true, // mobile taps
        worldCopyJump: true,
      }) as LeafletMap

      map.zoomControl.setPosition('topright')
      mapRef.current = map

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 10,
        minZoom: 1,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>',
      }).addTo(map)

      L.tileLayer(
        'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> contributors &copy; <a href="https://www.carto.com/">CARTO</a>',
          subdomains: 'abcd',
          maxZoom: 10,
          minZoom: 1,
        },
      ).addTo(map)

      const computeRadius = () =>
        Math.max(600, Math.min(window.innerWidth, 1000)) * 0.005

      const radius = computeRadius()
      const markers: LeafletCircleMarker[] = []

      for (const loc of networkLocations) {
        const m = L.circleMarker([loc.latitude, loc.longitude], {
          radius,
          stroke: true,
          weight: 2,
          color: '#f8fafc',
          fillOpacity: 1,
          fillColor: '#608bd8',
        })
          .bindTooltip(loc.name)
          .addTo(map)
        markers.push(m)
      }
      markersRef.current = markers

      const bounds = L.latLngBounds(
        markers.map((m) => m.getLatLng() as unknown as [number, number]),
      )

      if (bounds.isValid()) {
        map.fitBounds(bounds.pad(0.12), { animate: false }) // tighter than 0.2
        map.setZoom(map.getZoom() + 1) // extra zoom-in by 1 level
      } else {
        map.setView([20, 0], 3) // fallback default
      }

      setIsMapLoaded(true)

      const onResize = () => {
        if (!mapRef.current) return
        const r = computeRadius()
        markersRef.current.forEach((m) => m.setRadius(r))
        mapRef.current.invalidateSize()
      }
      window.addEventListener('resize', onResize)

      return () => {
        window.removeEventListener('resize', onResize)
        if (mapRef.current) {
          mapRef.current.remove()
          mapRef.current = null
        }
      }
    })()

    return () => {
      destroyed = true
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [])

  return (
    <div className={cx('relative rounded-lg overflow-hidden', className)}>
      {/* Fallback image */}
      <Image
        alt='Map with our edge locations, this map contains all of the 68 Fastly Points Of Presence we are caching in'
        src='/edge-location-map.webp'
        width={1280}
        height={537}
        priority
        className='rounded-lg'
      />

      <div
        className={`absolute inset-0 w-full h-full pr-10 rounded-lg overflow-hidden ${
          isMapLoaded ? 'bg-gray-50' : 'bg-transparent'
        }`}
      >
        <div
          ref={containerRef}
          id='map'
          style={{ width: '100%', height: '100%' }}
          className='rounded-lg overflow-hidden'
        />
      </div>
    </div>
  )
}

export const networkLocations = pops.map((pop) => ({
  name: pop.city,
  latitude: pop.latitude,
  longitude: pop.longitude,
  fillKey: 'single',
}))
